import React from 'react'
// libs
import styled from 'styled-components'
import { useSelector } from 'react-redux'
import { useMutation } from '@apollo/client/react'
import { formValueSelector, reduxForm } from 'redux-form'
// components
import ReduxWysiwyg from '../../ReduxWysiwyg'
import { PopoverModal } from 'components/UI'
// view components
import ActivityFilter from 'components/Modals/PlanItems/components/ActivitiesList/components/ActivitiesFilter'
import AttachmentsMenu from 'components/Modals/PlanItems/components/ActivitiesList/components/AddAttachmentsMenu'
// utils
import { isFunction } from 'lodash/fp'
import { handleError } from 'utils/errors/handlers'
// views
import { AddAttachmentIcon, FilterIcon, Form, SubmitButton, CloseIcon } from './views'
// icons
import SendRoundedIcon from '@mui/icons-material/SendRounded'
// constants
import { Forms, WYSIWIG_CLASS_NAMES } from 'constants/index'
// context
import { ActivitiesListAPIContext } from 'containers/Activities/layers/API/ActivitiesList'
import { ActivitiesListBLContext } from 'containers/Activities/layers/BL/ActivitiesList'
// graphql
import { CREATE_ACTIVITY_MUTATION, EDIT_ACTIVITY_MUTATION } from 'graphql/activities/mutations'
// types
import { LogActionTypes, LogEntityTypes } from 'types/graphql'
// utils
import { prepareHtml } from 'utils/html'

const WysiwygWrapper = styled.div`
  .${WYSIWIG_CLASS_NAMES.editorClassName} {
    max-height: 200px;
    padding-right: 12px;
  }

  .${WYSIWIG_CLASS_NAMES.toolbarClassName} {
    margin-right: 30px;
  }
`

const PAPER_PROPS = { style: { width: 160 } }
const WYSIWYG_TOOLBAR = { options: ['inline', 'history'] as const }

const CreateCommentForm = ({ open, form, handleSubmit, isSubComment, isCommentEdit, handleCloseForm, reset }) => {
  const { entity, refetch } = React.useContext(ActivitiesListAPIContext)
  const { handleAddLinkClick } = React.useContext(ActivitiesListBLContext)

  const [editCommentMutation] = useMutation(EDIT_ACTIVITY_MUTATION)
  const [createCommentMutation] = useMutation(CREATE_ACTIVITY_MUTATION)

  const selector = formValueSelector(form)

  const { activityId } = useSelector((state) => ({
    activityId: selector(state, 'activityId')
  }))

  const handleCreateComment = async (data, _, { reset }) => {
    try {
      const input = {
        entityType: LogEntityTypes.Comment,
        actionType: LogActionTypes.Create,
        metadata: {
          comment: prepareHtml(data.content)
        }
      }

      if (!activityId) {
        await createCommentMutation({
          variables: {
            input: {
              ...input,
              entityId: entity.id
            }
          }
        })
      } else {
        await editCommentMutation({
          variables: {
            id: activityId,
            input: {
              ...input,
              actionType: LogActionTypes.Update
            }
          }
        })
      }

      if (isFunction(refetch)) {
        await refetch()
      }

      if (isFunction(handleCloseForm)) {
        handleCloseForm()
      }

      reset()
    } catch (error) {
      handleError(error, 'CreateCommentForm.handleCreateComment')
    }
  }

  const onAddLinkClick = () => handleAddLinkClick(entity)

  React.useEffect(() => {
    return () => {
      reset()
    }
  }, [])

  return (
    <Form onSubmit={handleSubmit(handleCreateComment)} isCommentEdit={isCommentEdit}>
      {!isCommentEdit && (
        <>
          <PopoverModal trigger={<AddAttachmentIcon />} PaperProps={PAPER_PROPS}>
            {({ closePopup }) => (
              <AttachmentsMenu addAttachment={open} closePopup={closePopup} addLink={onAddLinkClick} />
            )}
          </PopoverModal>

          {!isSubComment && <PopoverModal trigger={<FilterIcon />}>{() => <ActivityFilter />}</PopoverModal>}
        </>
      )}

      {(isSubComment || isCommentEdit) && <CloseIcon onClick={handleCloseForm} />}
      <WysiwygWrapper>
        <ReduxWysiwyg name='content' toolbar={WYSIWYG_TOOLBAR} placeholder='Add comment...' />
      </WysiwygWrapper>
      <SubmitButton type='submit' size='small'>
        <SendRoundedIcon fontSize='small' />
      </SubmitButton>
    </Form>
  )
}

export default reduxForm<any, any>({
  form: Forms.CREATE_COMMENT_FORM,
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true
})(CreateCommentForm)
